import { useState, useEffect } from "react";
import { WebTextPlane } from "../text/WebTextPlane";
import { UI_EVENTS } from "@retreat/core";
import { formatDeepTime } from "../utils/deepTimeFormatter";
import { TEST_IDS } from "../testing/testIds";

interface RetreatDisplayProps {
  textPlane?: WebTextPlane;
  elapsedSeconds?: number;
}

/**
 * @deprecated M2-UI Phase: This component is replaced by LiveScreen.tsx.
 * Do not import or use in new code.
 */
export function RetreatDisplay({
  textPlane,
  elapsedSeconds = 0,
}: RetreatDisplayProps) {
  const [currentText, setCurrentText] = useState("");
  const [offsetSeconds, setOffsetSeconds] = useState(elapsedSeconds);

  useEffect(() => {
    if (!textPlane) return;

    const handleTextChange = ({
      text,
      offsetSeconds,
    }: {
      text: string;
      offsetSeconds: number;
    }) => {
      setCurrentText(text);
      setOffsetSeconds(offsetSeconds);
    };

    textPlane.on(UI_EVENTS.TEXT_CHANGE, handleTextChange);

    return () => {
      textPlane.off(UI_EVENTS.TEXT_CHANGE, handleTextChange);
    };
  }, [textPlane]);

  // Also sync the elapsed prop from RetreatBridge (RIGHT BICEP: C - Cross-check)
  useEffect(() => {
    setOffsetSeconds(elapsedSeconds);
  }, [elapsedSeconds]);

  // Deep Time: handles mm:ss  h:mm:ss  Day N · h:mm  Year N · Day N · h:mm
  // Supports retreats from seconds to 7 years (220,752,000 seconds).
  const formatTime = (seconds: number): string => formatDeepTime(seconds);

  return (
    <div className="flex flex-col items-center justify-center min-h-[160px] bg-white rounded-lg shadow-md p-4 max-w-xl mx-auto">
      <div className="text-center mb-6">
        <div className="text-3xl font-light text-gray-800 mb-1">
          {currentText || "Silence"}
        </div>
        <div className="text-sm text-gray-400 uppercase tracking-widest">
          {currentText ? "Current Practice" : "Awaiting Start"}
        </div>
      </div>

      <div
        className="flex items-center space-x-4 text-gray-600 elapsed-time"
        data-testid={TEST_IDS.ELAPSED_TIMER}
      >
        <div className="text-lg font-mono">{formatTime(offsetSeconds)}</div>
        <div className="text-sm">elapsed</div>
      </div>

      <div className="mt-6 w-full">
        <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-purple-600 transition-all duration-300"
            style={{ width: offsetSeconds > 0 ? "20%" : "0%" }}
          />
        </div>
      </div>
    </div>
  );
}
